/**
 * Los conectores, dibujados en el mundo GPU.
 *
 * Va **entre los recuadros de contenedor y las tarjetas**: así una flecha nunca
 * tapa el contenido de una tarjeta, pero sí se ve sobre el tinte del contenedor.
 *
 * Todo el trabajo de esta clase es no trabajar. La regla del README —nada se
 * recalcula por frame si no cambió su entrada— se aplica en tres puertas, de la
 * más barata a la más cara:
 *
 *  1. **Identidad de arrays.** El store es inmutable: si `cards` y `edges` son
 *     las mismas referencias que la última vez, nada pudo cambiar. Panear y
 *     quedarse quieto salen por acá sin tocar una sola arista.
 *  2. **Región pintada.** El lote se pinta para una región más grande que la
 *     pantalla; mientras el viewport siga adentro, panear no obliga a recortar
 *     de nuevo.
 *  3. **Firma por arista.** Recién si algo cambió se comparan los rects de los
 *     extremos, el `updatedAt` y la banda de zoom, campo por campo y sin
 *     construir strings.
 *
 * Cada arista tiene su propio `Graphics`, como cada tarjeta tiene su `CardView`.
 * Con un lote único, mover un nodo obligaba a repintar TODAS las aristas
 * visibles en cada frame del arrastre — trabajo por frame proporcional a lo que
 * hay en pantalla, que es exactamente lo que la regla prohíbe. Así se repintan
 * sólo las que tocan al nodo movido.
 */

import { Container, Graphics } from "pixi.js";
import {
  type Card,
  type Edge,
  DEFAULT_FROM_END,
  DEFAULT_TO_END,
  DEFAULT_ROUTE,
  DEFAULT_EDGE_COLOR,
} from "../model/types.ts";
import { clamp } from "./Camera.ts";
import { inkColor } from "./ink.ts";
import { UI_SELECTION, UI_PAPER } from "./uiColors.ts";
import {
  type Rect,
  arrowHead,
  bandZoom,
  endDirection,
  expandRect,
  rectContains,
  rectsIntersect,
  routeCullBounds,
  routePath,
  startDirection,
  autoSides,
  distanceToPath,
  pointAtFraction,
  zoomBand,
} from "./edgeGeometry.ts";

/** Grosor de una arista, en px de PANTALLA (se divide por el zoom al pintar). */
const EDGE_WIDTH = 1.6;
const EDGE_WIDTH_SELECTED = 2.4;

/** Funda bajo la línea, en px de pantalla: separa cruces y del relleno del nodo. */
const CASING_EXTRA = 4;
/** Resplandor de la seleccionada, en px de pantalla. */
const GLOW_EXTRA = 10;
/** Radio del redondeo de los codos, en px de pantalla. */
const CORNER_R = 7;

/** Fondo del canvas (ver `CanvasView.init`): color de la funda. */
const PAPER = UI_PAPER;

/**
 * Largo de la punta de flecha, en px de MUNDO: escala con el diagrama, igual
 * que las tarjetas, para que su tamaño RELATIVO no cambie al alejarse.
 *
 * OJO antes de "simplificar" esto a un solo número. Los dos extremos ya se
 * probaron y los dos se sienten mal, por razones opuestas:
 *
 *  - Fijo en px de MUNDO (sin tope): a zoom 12 la punta mide 120 px de
 *    pantalla sobre una línea de 1.6 px. Un triángulo absurdo.
 *  - Fijo en px de PANTALLA (`ARROW_SIZE / zoom`, sin piso ni tope): a zoom
 *    0.04 una tarjeta de 280 px mide 11 px, y una punta de 8 px queda casi tan
 *    grande como la tarjeta. Es el "enorme al alejarse" que motivó este cambio.
 *
 * Así que: proporcional al mundo en la franja de zoom donde uno de verdad
 * trabaja, y acotada en px de PANTALLA en los dos extremos para que ni domine
 * de lejos ni se infle de cerca.
 */
const ARROW_WORLD = 10;
/**
 * Piso en px de pantalla. De lejos la punta deja de encoger acá: con 2.5 px
 * quedaba demasiado tímida, con los 8 px fijos de antes tapaba las tarjetas.
 */
const ARROW_MIN_SCREEN = 5;
/** Tope en px de pantalla: de cerca no crece más que esto. */
const ARROW_MAX_SCREEN = 9;
/**
 * Cuánto se mete la punta DENTRO del objeto, en px de pantalla.
 *
 * `anchorPoint` devuelve un punto exacto del borde, así que el vértice ya lo
 * tocaba; pero justo ahí el antialiasing deja una hairline del fondo entre la
 * flecha y la tarjeta. Un mordisco chico la borra: como las tarjetas se pintan
 * por encima de las aristas, el sobrante queda tapado y el contacto se ve
 * limpio. También alarga el triángulo lo mismo, para no acortarlo por atrás.
 */
const ARROW_BITE = 1.5;

/** El mismo azul de selección de tarjetas y contenedores. */
const SELECTED_COLOR = UI_SELECTION;

/** Cuánto más grande que la pantalla se pinta el lote, en fracción de pantalla. */
const PAINT_OVERSCAN = 0.5;

/** Los datos de los que depende el dibujo de una arista. */
interface Signature {
  ax: number;
  ay: number;
  aw: number;
  ah: number;
  bx: number;
  by: number;
  bw: number;
  bh: number;
  updatedAt: number;
  band: number;
  selected: boolean;
}

/**
 * Las firmas se comparan y se escriben CONTRA campos sueltos, sin construir un
 * objeto por arista y por frame.
 *
 * No es microoptimización gratuita: en un arrastre `sync` corre 60 veces por
 * segundo sobre todas las aristas visibles, y alojar una firma por vuelta
 * generaba basura suficiente como para que el recolector se comiera un frame
 * entero cada tanto. El camino de arrastre no aloja nada.
 */
function sigMatches(
  s: Signature,
  a: Rect,
  b: Rect,
  updatedAt: number,
  band: number,
  selected: boolean,
): boolean {
  return (
    s.ax === a.x &&
    s.ay === a.y &&
    s.aw === a.width &&
    s.ah === a.height &&
    s.bx === b.x &&
    s.by === b.y &&
    s.bw === b.width &&
    s.bh === b.height &&
    s.updatedAt === updatedAt &&
    s.band === band &&
    s.selected === selected
  );
}

function writeSig(
  s: Signature,
  a: Rect,
  b: Rect,
  updatedAt: number,
  band: number,
  selected: boolean,
): void {
  s.ax = a.x;
  s.ay = a.y;
  s.aw = a.width;
  s.ah = a.height;
  s.bx = b.x;
  s.by = b.y;
  s.bw = b.width;
  s.bh = b.height;
  s.updatedAt = updatedAt;
  s.band = band;
  s.selected = selected;
}

function sameSet(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

/**
 * Traza la polilínea redondeando sus vértices con un filete de radio `radius`
 * (en px de mundo). Las curvas muestreadas vienen con decenas de puntos ya
 * suaves y se dibujan directas: el filete es sólo para los codos.
 */
function strokeSmooth(g: Graphics, path: number[], radius: number): void {
  if (path.length <= 4 || path.length > 16 || radius <= 0.01) {
    g.moveTo(path[0], path[1]);
    for (let i = 2; i + 1 < path.length; i += 2) g.lineTo(path[i], path[i + 1]);
    return;
  }
  const n = path.length / 2;
  const px = (i: number) => path[i * 2];
  const py = (i: number) => path[i * 2 + 1];
  g.moveTo(px(0), py(0));
  for (let i = 1; i < n - 1; i++) {
    const x0 = px(i - 1);
    const y0 = py(i - 1);
    const cx = px(i);
    const cy = py(i);
    const x1 = px(i + 1);
    const y1 = py(i + 1);
    const d1 = Math.hypot(cx - x0, cy - y0);
    const d2 = Math.hypot(x1 - cx, y1 - cy);
    if (d1 < 1e-6 || d2 < 1e-6) {
      g.lineTo(cx, cy);
      continue;
    }
    const r = Math.min(radius, d1 / 2, d2 / 2);
    const ax = cx - ((cx - x0) / d1) * r;
    const ay = cy - ((cy - y0) / d1) * r;
    const bx = cx + ((x1 - cx) / d2) * r;
    const by = cy + ((y1 - cy) / d2) * r;
    g.lineTo(ax, ay);
    g.quadraticCurveTo(cx, cy, bx, by);
  }
  g.lineTo(px(n - 1), py(n - 1));
}

export interface EdgeLayerSyncInput {
  edges: Edge[];
  cards: Card[];
  zoom: number;
  /** Rectángulo de mundo visible en pantalla. */
  viewport: Rect;
  /** Ids de aristas seleccionadas. */
  selected: ReadonlySet<string>;
}

export class EdgeLayer {
  readonly container = new Container();

  /** Aristas efectivamente repintadas en el último `sync`. El HUD lo muestra:
   *  si se mueve con el diagrama quieto, hay un bug. */
  repainted = 0;

  /** Un `Graphics` por arista, para poder repintar de a una. */
  private views = new Map<string, Graphics>();
  /** Las aristas van en su propio contenedor para que el preview quede encima. */
  private edges = new Container();
  /**
   * Las puntas de flecha, aparte de la línea.
   *
   * La línea se congela por banda de zoom (ver `zoomBand`): se repinta sólo al
   * cruzar una, con el zoom NOMINAL de la banda. Eso alcanza para un trazo de
   * 1 px —el error adentro de la banda es invisible—, pero en una punta de
   * 9 px el escalón del 10% entre bandas SÍ se ve. Así que las puntas se
   * repintan por frame, con el zoom EXACTO, y quedan de un tamaño de pantalla
   * fijo de verdad. Son cuatro triángulos por arista visible: barato.
   */
  private arrows = new Graphics();
  private preview = new Graphics();
  /** Último zoom con el que se pintaron las puntas. */
  private lastArrowZoom = NaN;

  private sigs = new Map<string, Signature>();
  /**
   * La ruta con la que quedó pintada cada arista visible.
   *
   * Se guarda para que el hit-test corra contra lo que está EN PANTALLA y no
   * contra un recálculo: cerca de la diagonal dos cálculos seguidos podrían
   * dar rutas distintas y el click no agarraría donde se ve la línea.
   */
  private paths = new Map<string, number[]>();

  // Estado de las tres puertas.
  private lastCards: Card[] | null = null;
  private lastEdges: Edge[] | null = null;
  private lastSelected: ReadonlySet<string> = new Set();
  private lastBand = NaN;
  private painted: Rect | null = null;

  /** Índice id → rect, reconstruido sólo cuando cambia el array de tarjetas. */
  private rects = new Map<string, Rect>();

  constructor() {
    this.container.eventMode = "none";
    this.edges.eventMode = "none";
    this.arrows.eventMode = "none";
    this.container.addChild(this.edges, this.arrows, this.preview);
  }

  sync(input: EdgeLayerSyncInput): void {
    const { edges, cards, zoom, viewport, selected } = input;
    this.repainted = 0;
    const band = zoomBand(zoom);

    // Puerta 1 + 2: nada cambió y el viewport sigue adentro de lo pintado.
    if (
      cards === this.lastCards &&
      edges === this.lastEdges &&
      band === this.lastBand &&
      this.painted !== null &&
      rectContains(this.painted, viewport) &&
      sameSet(selected, this.lastSelected)
    ) {
      // La línea queda como está, pero las puntas siguen al zoom exacto: si
      // cambió, se rebaquean para no escalar con el mundo.
      if (zoom !== this.lastArrowZoom) {
        this.paintArrows(edges, selected, zoom);
        this.lastArrowZoom = zoom;
      }
      return;
    }

    if (cards !== this.lastCards) {
      // Se mutan los rects que ya existen en vez de rearmar el índice entero:
      // durante un arrastre esto corre en cada frame, y alojar 500 objetos por
      // frame es justamente la basura que hace saltar al recolector.
      for (const c of cards) {
        const r = this.rects.get(c.id);
        if (r) {
          r.x = c.x;
          r.y = c.y;
          r.width = c.width;
          r.height = c.height;
        } else {
          this.rects.set(c.id, { x: c.x, y: c.y, width: c.width, height: c.height });
        }
      }
      // Podar sólo cuando sobra algo, que es cuando se borró una tarjeta.
      if (this.rects.size !== cards.length) {
        const live = new Set(cards.map((c) => c.id));
        for (const id of [...this.rects.keys()]) if (!live.has(id)) this.rects.delete(id);
      }
    }

    // Región para la que se pinta: más grande que la pantalla, así panear un
    // poco no obliga a recortar de nuevo.
    const overscan = Math.max(viewport.width, viewport.height) * PAINT_OVERSCAN;
    const region = expandRect(viewport, overscan);

    this.painted = region;
    this.lastCards = cards;
    this.lastEdges = edges;
    this.lastBand = band;
    this.lastSelected = new Set(selected);

    // Se pinta con el zoom NOMINAL de la banda, no con el exacto: así el grosor
    // es estable adentro de la banda en vez de saltar en cada frame de un zoom.
    const paintZoom = bandZoom(band);

    // Puerta 3: por arista, se repinta sólo la que cambió de verdad.
    const alive = new Set<string>();
    for (const edge of edges) {
      const a = this.rects.get(edge.fromNode);
      const b = this.rects.get(edge.toNode);
      // Huérfana transitoria (entre un borrado y el próximo sync): no se dibuja.
      if (!a || !b) continue;
      if (!rectsIntersect(routeCullBounds(a, b), region)) continue;
      alive.add(edge.id);

      const isSelected = selected.has(edge.id);
      let sig = this.sigs.get(edge.id);
      if (sig && sigMatches(sig, a, b, edge.updatedAt, band, isSelected)) continue;
      if (!sig) {
        sig = {} as Signature;
        this.sigs.set(edge.id, sig);
      }
      writeSig(sig, a, b, edge.updatedAt, band, isSelected);

      let g = this.views.get(edge.id);
      if (!g) {
        g = new Graphics();
        g.eventMode = "none";
        this.views.set(edge.id, g);
        this.edges.addChild(g);
      }
      g.clear();
      this.paths.set(edge.id, this.paintEdge(g, edge, a, b, paintZoom, isSelected));
      this.repainted++;
    }

    // Las que salieron de la vista o del documento sueltan su Graphics.
    for (const [id, g] of this.views) {
      if (alive.has(id)) continue;
      g.destroy();
      this.views.delete(id);
      this.sigs.delete(id);
      this.paths.delete(id);
    }

    this.paintArrows(edges, selected, zoom);
    this.lastArrowZoom = zoom;
  }

  /**
   * Repinta TODAS las puntas de flecha visibles, con el zoom exacto.
   *
   * Corre por frame (es barato: dos triángulos por extremo con punta) y no por
   * banda de zoom como la línea: entre el piso y el tope el tamaño varía de
   * forma continua con el zoom, así que redondearlo a la banda se vería como
   * escalones. Usa las rutas ya guardadas en `this.paths` —la posición y la
   * dirección de la punta no dependen del zoom, sólo su tamaño—, así que
   * alcanza con volver a emitir los triángulos.
   */
  private paintArrows(
    edges: Edge[],
    selected: ReadonlySet<string>,
    zoom: number,
  ): void {
    const g = this.arrows;
    g.clear();
    // Proporcional al mundo, pero acotada por lo que mide en PANTALLA. Se
    // vuelve a dividir por el zoom porque `Graphics` dibuja en px de mundo.
    const size = clamp(ARROW_WORLD * zoom, ARROW_MIN_SCREEN, ARROW_MAX_SCREEN) / zoom;
    const bite = ARROW_BITE / zoom;
    for (const edge of edges) {
      const path = this.paths.get(edge.id);
      if (!path || path.length < 4) continue;
      const color = selected.has(edge.id)
        ? SELECTED_COLOR
        : inkColor(edge.color ?? DEFAULT_EDGE_COLOR);
      if ((edge.toEnd ?? DEFAULT_TO_END) === "arrow") {
        const dir = endDirection(path);
        const tip = {
          x: path[path.length - 2] + dir.x * bite,
          y: path[path.length - 1] + dir.y * bite,
        };
        g.poly(arrowHead(tip, dir, size + bite)).fill(color);
      }
      if ((edge.fromEnd ?? DEFAULT_FROM_END) === "arrow") {
        const dir = startDirection(path);
        const tip = { x: path[0] + dir.x * bite, y: path[1] + dir.y * bite };
        g.poly(arrowHead(tip, dir, size + bite)).fill(color);
      }
    }
  }

  private paintEdge(
    g: Graphics,
    edge: Edge,
    a: Rect,
    b: Rect,
    zoom: number,
    selected: boolean,
  ): number[] {
    // Los lados explícitos mandan siempre. Sin ellos (aristas de documentos
    // viejos) se calcula una vez por posición: no hay memoria del par
    // anterior, así que nada reacomoda lados solo.
    const auto = autoSides(a, b);
    const sideA = edge.fromSide ?? auto[0];
    const sideB = edge.toSide ?? auto[1];

    const path = routePath(a, sideA, b, sideB, edge.route ?? DEFAULT_ROUTE);
    if (path.length < 4) return path;

    const color = selected ? SELECTED_COLOR : inkColor(edge.color ?? DEFAULT_EDGE_COLOR);
    const width = (selected ? EDGE_WIDTH_SELECTED : EDGE_WIDTH) / zoom;
    const corner = CORNER_R / zoom;

    // Resplandor de la seleccionada: halo translúcido por fuera de la funda.
    if (selected) {
      strokeSmooth(g, path, corner);
      g.stroke({
        width: width + GLOW_EXTRA / zoom,
        color: SELECTED_COLOR,
        alpha: 0.16,
        cap: "round",
        join: "round",
      });
    }
    // Funda: separa la línea de cruces, etiquetas y rellenos de nodos.
    strokeSmooth(g, path, corner);
    g.stroke({ width: width + CASING_EXTRA / zoom, color: PAPER, cap: "round", join: "round" });
    strokeSmooth(g, path, corner);
    g.stroke({ width, color, cap: "round", join: "round" });

    // Las puntas de flecha las pinta `paintArrows`, por frame y con el zoom
    // exacto, para que queden de un tamaño de pantalla fijo.
    return path;
  }

  /**
   * La arista visible más cercana al punto de mundo, dentro de `tolerance`.
   *
   * Sólo considera las que están dibujadas: no se puede clickear una flecha que
   * no se ve, y así el costo es proporcional a lo que hay en pantalla.
   */
  hitTest(wx: number, wy: number, tolerance: number): string | null {
    let best: string | null = null;
    let bestDist = tolerance;
    for (const [id, path] of this.paths) {
      const d = distanceToPath(path, wx, wy);
      if (d <= bestDist) {
        bestDist = d;
        best = id;
      }
    }
    return best;
  }

  /**
   * La arista que se está dibujando ahora mismo (entrega 3). Vive en su propio
   * `Graphics` para que cambiar por frame no obligue a rearmar el lote.
   * Con funda y punto final para leer el destino de un vistazo.
   */
  paintPreview(path: number[] | null, zoom: number): void {
    this.preview.clear();
    if (!path || path.length < 4) return;
    const corner = CORNER_R / zoom;
    strokeSmooth(this.preview, path, corner);
    this.preview.stroke({
      width: (EDGE_WIDTH_SELECTED + CASING_EXTRA) / zoom,
      color: PAPER,
      cap: "round",
      join: "round",
    });
    strokeSmooth(this.preview, path, corner);
    this.preview.stroke({
      width: EDGE_WIDTH_SELECTED / zoom,
      color: SELECTED_COLOR,
      cap: "round",
      join: "round",
    });
    const ex = path[path.length - 2];
    const ey = path[path.length - 1];
    this.preview.circle(ex, ey, (EDGE_WIDTH_SELECTED + 2.5) / zoom);
    this.preview.fill(PAPER);
    this.preview.circle(ex, ey, EDGE_WIDTH_SELECTED / zoom);
    this.preview.fill(SELECTED_COLOR);
  }

  /** Fuerza un repintado completo en el próximo `sync`. */
  invalidate(): void {
    this.painted = null;
    this.lastCards = null;
    this.lastEdges = null;
    this.sigs.clear();
  }

  /** ¿Está esta arista dibujada ahora mismo? */
  isVisible(id: string): boolean {
    return this.paths.has(id);
  }

  /** Punto medio (por largo) de una arista visible: donde va su etiqueta. */
  midpointOf(id: string): { x: number; y: number } | null {
    const path = this.paths.get(id);
    return path ? pointAtFraction(path, 0.5) : null;
  }

  /** Los dos extremos de una arista visible, en coordenadas de mundo. */
  endpointsOf(id: string): [{ x: number; y: number }, { x: number; y: number }] | null {
    const p = this.paths.get(id);
    if (!p || p.length < 4) return null;
    return [
      { x: p[0], y: p[1] },
      { x: p[p.length - 2], y: p[p.length - 1] },
    ];
  }

  destroy(): void {
    this.container.removeFromParent();
    for (const g of this.views.values()) g.destroy();
    this.views.clear();
    this.preview.destroy();
    this.arrows.destroy();
    this.edges.destroy();
    this.container.destroy();
  }
}
